import React, { useState, useEffect, useRef, useMemo } from 'react';
import Map, { Source, Layer, Marker, Popup } from 'react-map-gl/mapbox-legacy';
import './activeCompsMap.css';
import { processListingsForMap } from '../utils/mapUtils';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || process.env.VITE_MAPBOX_TOKEN;

// Dark theme map style - using darker style to match scatter chart
const DARK_MAP_STYLE = 'mapbox://styles/mapbox/dark-v11';

export default function ActiveCompsMap({
  activeListings = [],
  dealerLat,
  dealerLng,
  hooptiePrice,
  targetMiles,
  marketAvgDom,
  onVisibleVinsChange,
  visibleVins = new Set()
}) {
  const [popupInfo, setPopupInfo] = useState(null);
  const [viewport, setViewport] = useState({
    latitude: dealerLat || 37.5483,
    longitude: dealerLng || -121.9886,
    zoom: 9
  });
  const mapRef = useRef(null);

  // Process listings for map
  const { processed: mapListings, missingLocationCount } = useMemo(() => {
    if (!dealerLat || !dealerLng) {
      return { processed: [], missingLocationCount: 0 };
    }
    return processListingsForMap(
      activeListings,
      dealerLat,
      dealerLng,
      100, // 100 miles radius
      hooptiePrice,
      targetMiles,
      marketAvgDom
    );
  }, [activeListings, dealerLat, dealerLng, hooptiePrice, targetMiles, marketAvgDom]);

  // Create GeoJSON for heatmap
  const heatmapData = useMemo(() => {
    if (mapListings.length === 0) return null;

    return {
      type: 'FeatureCollection',
      features: mapListings.map(listing => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [listing.lng, listing.lat]
        },
        properties: {
          vin: listing.vin,
          weight: listing.weight,
          price: listing.price,
          miles: listing.miles,
          dom_active: listing.dom_active,
          dealer_name: listing.dealer_name,
          vdp_url: listing.vdp_url,
          photo: listing.photo,
          distance: listing.distance,
          heading: listing.heading || listing.title
        }
      }))
    };
  }, [mapListings]);

  // Update visible VINs when map moves
  useEffect(() => {
    if (!mapRef.current || !onVisibleVinsChange) return;

    // For legacy version, the ref might be the map instance directly or need getMap()
    const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
    if (!map || !map.getBounds) return;

    const updateVisibleVins = () => {
      try {
        const bounds = map.getBounds();
        if (!bounds) return;

        const visible = mapListings.filter(listing => {
          return bounds.contains([listing.lng, listing.lat]);
        }).map(l => l.vin);

        onVisibleVinsChange(new Set(visible));
      } catch (e) {
        // Map might not be ready yet
        console.warn('Map bounds not available:', e);
      }
    };

    map.on('moveend', updateVisibleVins);
    // Delay initial call to ensure map is ready
    setTimeout(updateVisibleVins, 100);

    return () => {
      if (map.off) {
        map.off('moveend', updateVisibleVins);
      }
    };
  }, [mapListings, onVisibleVinsChange]);

  // Debug logging (must be before any early return to keep hook order consistent)
  useEffect(() => {
    console.log('ActiveCompsMap Debug:', {
      mapListingsCount: mapListings.length,
      dealerLat,
      dealerLng,
      hasToken: !!MAPBOX_TOKEN,
      heatmapData: !!heatmapData
    });
  }, [mapListings.length, dealerLat, dealerLng, heatmapData]);

  // Heatmap layer configuration
  const heatmapLayer = {
    id: 'heatmap',
    type: 'heatmap',
    paint: {
      'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0, 2, 1],
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 3],
      'heatmap-color': [
        'interpolate',
        ['linear'],
        ['heatmap-density'],
        0, 'rgba(33, 102, 172, 0)',
        0.2, 'rgba(103, 169, 207, 0.5)',
        0.4, 'rgba(209, 229, 240, 0.8)',
        0.6, 'rgba(253, 219, 199, 0.9)',
        0.8, 'rgba(239, 138, 98, 1)',
        1, 'rgba(178, 24, 43, 1)'
      ],
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 20],
      'heatmap-opacity': 0.6
    }
  };

  // Circle layer for individual points (visible at zoom >= 10)
  const circleLayer = {
    id: 'points',
    type: 'circle',
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 4, 12, 8],
      'circle-color': [
        'interpolate',
        ['linear'],
        ['get', 'weight'],
        0.2, '#2166ac',
        1.0, '#fee5d9',
        2.0, '#b2182b'
      ],
      'circle-stroke-width': 1,
      'circle-stroke-color': '#fff',
      'circle-opacity': 0.7
    }
  };

  // Handle point click
  const handlePointClick = (event) => {
    const feature = event.features?.[0];
    if (feature) {
      setPopupInfo({
        ...feature.properties,
        lng: feature.geometry.coordinates[0],
        lat: feature.geometry.coordinates[1]
      });
    }
  };

  // Edge cases
  if (!dealerLat || !dealerLng) {
    return (
      <div className="active-comps-map-container">
        <div className="map-empty-state">
          <p>Set dealer location to view map.</p>
        </div>
      </div>
    );
  }

  if (mapListings.length < 3) {
    return (
      <div className="active-comps-map-container">
        <div className="map-empty-state">
          <p>Not enough geocoded listings to render heatmap.</p>
          {missingLocationCount > 0 && (
            <p className="map-missing-count">{missingLocationCount} listings missing location</p>
          )}
        </div>
      </div>
    );
  }

  if (!MAPBOX_TOKEN) {
    return (
      <div className="active-comps-map-container">
        <div className="map-empty-state">
          <p>Mapbox token not configured. Please set REACT_APP_MAPBOX_TOKEN in your .env file.</p>
          <p className="map-missing-count">Add: REACT_APP_MAPBOX_TOKEN=your_token_here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="active-comps-map-container">
      <Map
        ref={mapRef}
        initialViewState={viewport}
        onMove={evt => setViewport(evt.viewState)}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={DARK_MAP_STYLE}
        style={{ width: '100%', height: '500px', display: 'block' }}
        interactiveLayerIds={['points']}
        onClick={handlePointClick}
        onError={(e) => {
          console.error('Map error:', e);
        }}
        onLoad={() => {
          console.log('Map loaded successfully');
        }}
      >
        {/* Dealer marker */}
        <Marker
          latitude={dealerLat}
          longitude={dealerLng}
          anchor="center"
        >
          <div className="dealer-marker">
            <div className="dealer-marker-pin"></div>
          </div>
        </Marker>

        {/* Radius rings - using fill and line layers */}
        {/* 50 mile radius ring */}
        <Source
          id="radius-50"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [dealerLng, dealerLat]
            }
          }}
        >
          <Layer
            id="radius-50-circle"
            type="circle"
            paint={{
              'circle-radius': {
                stops: [
                  [0, 0],
                  [6, 50 * 1609.34 / 100],
                  [9, 50 * 1609.34 / 10],
                  [12, 50 * 1609.34]
                ],
                base: 2
              },
              'circle-color': 'rgba(96, 165, 250, 0.1)',
              'circle-stroke-color': 'rgba(96, 165, 250, 0.5)',
              'circle-stroke-width': 2
            }}
          />
        </Source>

        {/* 100 mile radius ring (dashed) */}
        <Source
          id="radius-100"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [dealerLng, dealerLat]
            }
          }}
        >
          <Layer
            id="radius-100-circle"
            type="circle"
            paint={{
              'circle-radius': {
                stops: [
                  [0, 0],
                  [6, 100 * 1609.34 / 100],
                  [9, 100 * 1609.34 / 10],
                  [12, 100 * 1609.34]
                ],
                base: 2
              },
              'circle-color': 'transparent',
              'circle-stroke-color': 'rgba(96, 165, 250, 0.3)',
              'circle-stroke-width': 1
            }}
          />
        </Source>

        {/* Heatmap layer */}
        {heatmapData && (
          <Source id="heatmap-data" type="geojson" data={heatmapData}>
            <Layer {...heatmapLayer} />
            <Layer
              {...circleLayer}
              layout={{
                visibility: viewport.zoom >= 10 ? 'visible' : 'none'
              }}
            />
          </Source>
        )}

        {/* Popup */}
        {popupInfo && (
          <Popup
            latitude={popupInfo.lat}
            longitude={popupInfo.lng}
            closeButton={true}
            closeOnClick={false}
            onClose={() => setPopupInfo(null)}
            anchor="bottom"
            className="listing-popup"
            maxWidth="280px"
            closeOnMove={false}
          >
            <div className="popup-content">
              <div className="popup-price">${popupInfo.price.toLocaleString()}</div>
              <div className="popup-details">
                <div>Miles: {popupInfo.miles.toLocaleString()}</div>
                <div>Dealer: {popupInfo.dealer_name}</div>
                {popupInfo.distance !== undefined && (
                  <div className="popup-distance">
                    Distance: {popupInfo.distance.toFixed(1)} mi
                  </div>
                )}
              </div>
              {popupInfo.vdp_url && (
                <a
                  href={popupInfo.vdp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="popup-link"
                >
                  View VDP →
                </a>
              )}
            </div>
          </Popup>
        )}
      </Map>

      {/* Footer with missing location count */}
      {missingLocationCount > 0 && (
        <div className="map-footer">
          {missingLocationCount} listing{missingLocationCount !== 1 ? 's' : ''} missing location
        </div>
      )}
    </div>
  );
}

